import React from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';
import { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import AppRouter from './AppRouter';
import './index.css';
import { ClerkProvider } from '@clerk/clerk-react';

import { registerCloseBeacon } from './lib/sessionClose';
import { computeAllowedRedirectOrigins } from './lib/redirectOrigins';
import ErrorBoundary from './components/system/ErrorBoundary';
import { Toaster } from 'sonner';
import { config } from './lib/client-config';
import { makeMutationCacheOnError, makeQueryCacheOnError } from './lib/mutation-cache-handler';
import { makeUnhandledRejectionHandler } from './lib/unhandled-rejection-handler';
import { applyStoredTheme } from './lib/theme';

// Apply the user's stored theme before the first paint. Home has no shell of
// its own that calls `useMiniAppDarkMode` (unlike /tools and /flow), so
// without this, a session that starts on Home renders with no theme applied
// until the user happens to visit a mini-app or Flow shell.
applyStoredTheme();

// One-shot purge of legacy renderer keys left by users who visited before the
// toggle was removed. Neither key is read anymore; removing them keeps
// localStorage clean and prevents confusion in future DevTools audits.
try {
  localStorage.removeItem('terra:renderer:v2');
  localStorage.removeItem('terra:renderer');
} catch {
  // Private browsing or storage full — silently degrade.
}

// Load React Grab in development mode for ⌘C context copying
if (import.meta.env.DEV) {
  void import('react-grab');
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: makeQueryCacheOnError(),
  }),
  mutationCache: new MutationCache({
    onError: makeMutationCacheOnError(),
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnReconnect: true,
      refetchOnWindowFocus: false,
      staleTime: 0,
    },
    mutations: {
      retry: 0,
    },
  },
});
if (import.meta.env.DEV) void import('./lib/dev-bridge').then(({ installDevBridge }) => installDevBridge(queryClient));
const PUBLISHABLE_KEY = config.clerkPublishableKey();

// Compute allowed redirect origins so Clerk accepts redirect_url for cross-origin returns
const allowedRedirectOrigins = computeAllowedRedirectOrigins();

// Install close beacon listeners before React mounts
try { registerCloseBeacon(); } catch {}

// Global net for FetchError-shaped rejections that never reach React Query
// and were never caught locally (see unhandled-rejection-handler.ts).
window.addEventListener('unhandledrejection', makeUnhandledRejectionHandler());

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ClerkProvider publishableKey={PUBLISHABLE_KEY} afterSignOutUrl="/" allowedRedirectOrigins={allowedRedirectOrigins}>
      {/* Always render the app router so unauthenticated users can reach /sign-in */}
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <NuqsAdapter defaultOptions={{ clearOnDefault: false }}>
            <SkeletonTheme baseColor="var(--color-nightshade-200)" highlightColor="var(--color-nightshade-50)">
            <ErrorBoundary>
              <AppRouter />
            </ErrorBoundary>
            </SkeletonTheme>
            <Toaster position="bottom-right" richColors />
          </NuqsAdapter>
        </BrowserRouter>
      </QueryClientProvider>
    </ClerkProvider>
  </React.StrictMode>
);
